/**
 * Routes whose global-loading overlay shows the page's own skeleton instead
 * of the app's loading animation. The overlay covers every early phase of a
 * cold load (route chunk, auth, course data), so this is the one place a
 * route can opt out of the animation for all of them at once.
 */

import type {ComponentType} from 'react';
import styled from 'styled-components';
import {
  PortfolioAppPageSkeleton,
  PortfolioPageSkeleton,
} from 'mathide/portfolio/PortfolioSkeleton';

/** URL subpage (second path segment) of the student portfolio routes. */
const PORTFOLIO_SUBPAGE = 'portfolio';

/**
 * Whether a path is one of the student portfolio routes
 * (`/:courseId/portfolio`, `/:courseId/portfolio/:uid`,
 * `/:courseId/portfolio/:uid/:assignmentId`).
 *
 * @param pathname - The location's pathname.
 * @returns True for the portfolio routes.
 * @throws Never.
 */
export function isPortfolioRoute(pathname: string): boolean {
  return pathSegments(pathname)[1] === PORTFOLIO_SUBPAGE;
}

function pathSegments(pathname: string): string[] {
  return pathname.split('/').filter(segment => segment !== '');
}

function PortfolioOverlay() {
  return (
    <FullBleed>
      <PortfolioPageSkeleton />
    </FullBleed>
  );
}

function PortfolioAppOverlay() {
  return (
    <FullBleed>
      <PortfolioAppPageSkeleton />
    </FullBleed>
  );
}

/**
 * Picks the skeleton page for a route that has one.
 *
 * @param pathname - The location's pathname.
 * @returns The skeleton page component, or `null` when the route uses the
 *   app's default loading content.
 * @throws Never.
 */
export function resolveRouteLoadingPage(pathname: string): ComponentType | null {
  if (!isPortfolioRoute(pathname)) return null;
  // Four segments is the read-only app view; fewer is a portfolio grid.
  return pathSegments(pathname).length >= 4 ? PortfolioAppOverlay : PortfolioOverlay;
}

/* The overlay centers its content; a page skeleton wants the whole viewport. */
const FullBleed = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: #fff;
`;
